import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Loader2, Send, FlaskConical, BellRing, Zap } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";

function fmtDate(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/**
 * Iter-35 — Relance formation : email brandé (Resend) envoyé aux apprenants
 * à 0 point qui n'ont jamais commencé la formation.
 */
export default function TrainingReminderSection() {
    const confirm = useConfirm();
    const [learners, setLearners] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [sending, setSending] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testSending, setTestSending] = useState(false);
    // Iter-36 — réglages relance automatique
    const [autoCfg, setAutoCfg] = useState(null);
    const [autoSaving, setAutoSaving] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get("/admin/engagement/inactive-learners");
            setLearners(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
            setLearners([]);
        }
    };
    useEffect(() => {
        reload();
        api.get("/admin/engagement/auto-reminder")
            .then(({ data }) => setAutoCfg(data))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveAuto = async () => {
        setAutoSaving(true);
        try {
            const { data } = await api.put("/admin/engagement/auto-reminder", {
                enabled: autoCfg.enabled,
                days: Number(autoCfg.days) || 7,
                max_reminders: Number(autoCfg.max_reminders) || 3,
            });
            setAutoCfg((prev) => ({ ...data, last_run: prev?.last_run }));
            toast.success(
                data.enabled
                    ? `Relance automatique activée : tous les ${data.days} jours ✅`
                    : "Relance automatique désactivée",
            );
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setAutoSaving(false);
        }
    };

    const allSelected = useMemo(
        () => learners?.length > 0 && selected.size === learners.length,
        [learners, selected],
    );

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected(allSelected ? new Set() : new Set(learners.map((l) => l.user_id)));
    };

    const sendReminders = async () => {
        const ids = [...selected];
        const ok = await confirm({
            title: `Envoyer le mail de relance à ${ids.length} apprenant·e·s ?`,
            description:
                "Chaque personne sélectionnée recevra le mail brandé « Ta formation Shariti Academy t'attend » depuis recrutement@shariti.com.",
            confirmLabel: "Envoyer",
        });
        if (!ok) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/engagement/training-reminder", {
                user_ids: ids,
            });
            if (data.failed?.length) {
                toast.warning(
                    `${data.sent_count} mail(s) envoyé(s), ${data.failed.length} échec(s) (${data.failed
                        .map((f) => f.email)
                        .join(", ")})`,
                );
            } else {
                toast.success(`${data.sent_count} mail(s) de relance envoyé(s) ✉️`);
            }
            setSelected(new Set());
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    };

    const sendTest = async () => {
        if (!testEmail.trim()) {
            toast.error("Renseigne une adresse email de test.");
            return;
        }
        setTestSending(true);
        try {
            const { data } = await api.post("/admin/engagement/training-reminder/test", {
                email: testEmail.trim(),
            });
            toast.success(`Mail test envoyé à ${data.to} ✉️`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur d'envoi du test");
        } finally {
            setTestSending(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl p-5 lg:p-6"
            data-testid="training-reminder-section"
        >
            <div className="flex items-start gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                    <BellRing className="w-5 h-5 text-shariti-orange" />
                </div>
                <div>
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                        Relance formation
                    </p>
                    <h3 className="text-xl font-extrabold text-white tracking-tight">
                        Apprenants qui n'ont pas commencé
                    </h3>
                    <p className="text-xs text-white/55 font-light mt-1 max-w-2xl">
                        Ces apprenants ont 0 point : ils n'ont jamais suivi de cours. Sélectionne-les
                        et envoie-leur un joli mail de rappel (formation obligatoire pour être
                        opérationnel sur le terrain), expédié depuis recrutement@shariti.com.
                    </p>
                </div>
            </div>

            {/* Test email */}
            <div className="flex flex-col sm:flex-row gap-2 mb-5 p-3 rounded-2xl bg-white/5 border border-white/10">
                <div className="relative flex-1">
                    <FlaskConical className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                        type="email"
                        placeholder="Adresse pour recevoir un mail test (ex: toi-même)"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        data-testid="reminder-test-email-input"
                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/30 outline-none focus:border-shariti-orange/40"
                    />
                </div>
                <button
                    type="button"
                    onClick={sendTest}
                    disabled={testSending}
                    data-testid="reminder-test-send"
                    className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-all disabled:opacity-60"
                >
                    {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    M'envoyer un test
                </button>
            </div>

            {/* Iter-36 — Relance automatique */}
            {autoCfg && (
                <div
                    className="mb-5 p-4 rounded-2xl bg-white/5 border border-white/10"
                    data-testid="auto-reminder-block"
                >
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-shariti-yellow" />
                            <p className="text-sm font-extrabold text-white">
                                Relance automatique
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={autoCfg.enabled}
                            onClick={() => setAutoCfg({ ...autoCfg, enabled: !autoCfg.enabled })}
                            data-testid="auto-reminder-toggle"
                            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                                autoCfg.enabled ? "bg-shariti-orange" : "bg-white/15"
                            }`}
                        >
                            <span
                                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                                    autoCfg.enabled ? "translate-x-5" : ""
                                }`}
                            />
                        </button>
                    </div>
                    <p className="text-xs text-white/55 font-light mb-3">
                        Envoie tout seul le mail de rappel aux apprenants toujours à 0 point.
                        Vérification chaque jour à 10h : première relance après le délai
                        ci-dessous, puis re-relance au même rythme, dans la limite du maximum
                        par personne.
                    </p>
                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1">
                                Après (jours)
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={90}
                                value={autoCfg.days}
                                onChange={(e) => setAutoCfg({ ...autoCfg, days: e.target.value })}
                                data-testid="auto-reminder-days-input"
                                className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:border-shariti-orange/40"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1">
                                Relances max / pers.
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={10}
                                value={autoCfg.max_reminders}
                                onChange={(e) =>
                                    setAutoCfg({ ...autoCfg, max_reminders: e.target.value })
                                }
                                data-testid="auto-reminder-max-input"
                                className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:border-shariti-orange/40"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={saveAuto}
                            disabled={autoSaving}
                            data-testid="auto-reminder-save"
                            className="inline-flex items-center gap-2 bg-shariti-orange/20 hover:bg-shariti-orange/30 border border-shariti-orange/40 text-shariti-orange-light font-extrabold text-xs uppercase tracking-widest px-4 py-2.5 rounded-xl transition-all disabled:opacity-60"
                        >
                            {autoSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Enregistrer
                        </button>
                    </div>
                    {autoCfg.last_run?.last_run_at && (
                        <p
                            className="text-[11px] text-white/45 mt-3"
                            data-testid="auto-reminder-last-run"
                        >
                            Dernière exécution : {fmtDate(autoCfg.last_run.last_run_at)} —{" "}
                            {autoCfg.last_run.sent_count} envoyé(s)
                            {autoCfg.last_run.failed_count > 0 &&
                                `, ${autoCfg.last_run.failed_count} échec(s)`}
                        </p>
                    )}
                </div>
            )}

            {learners === null ? (
                <div className="h-24 rounded-2xl bg-white/5 animate-pulse" />
            ) : learners.length === 0 ? (
                <p className="text-sm text-white/50 text-center py-6" data-testid="reminder-empty">
                    🎉 Tous les apprenants ont déjà commencé leur formation.
                </p>
            ) : (
                <>
                    <label className="flex items-center gap-2.5 px-3 py-2 mb-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            data-testid="reminder-select-all"
                            className="w-4 h-4 accent-[#EE722E]"
                        />
                        <span className="text-xs font-bold text-white/70 uppercase tracking-widest">
                            Tout sélectionner ({learners.length})
                        </span>
                    </label>
                    <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                        {learners.map((l) => (
                            <label
                                key={l.user_id}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl border cursor-pointer transition-all ${
                                    selected.has(l.user_id)
                                        ? "bg-shariti-orange/10 border-shariti-orange/40"
                                        : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                                data-testid={`reminder-row-${l.user_id}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.has(l.user_id)}
                                    onChange={() => toggle(l.user_id)}
                                    data-testid={`reminder-check-${l.user_id}`}
                                    className="w-4 h-4 accent-[#EE722E] shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-white truncate">{l.name}</p>
                                    <p className="text-xs text-white/50 truncate">{l.email}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    {l.training_reminder_last_sent_at ? (
                                        <p className="text-[11px] text-shariti-yellow/90">
                                            Relancé le {fmtDate(l.training_reminder_last_sent_at)}
                                            {l.training_reminder_count > 1 && ` (×${l.training_reminder_count})`}
                                        </p>
                                    ) : (
                                        <p className="text-[11px] text-white/35">Jamais relancé</p>
                                    )}
                                </div>
                            </label>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={sendReminders}
                        disabled={sending || selected.size === 0}
                        data-testid="reminder-send-button"
                        className="w-full mt-4 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sending
                            ? "Envoi en cours..."
                            : `Envoyer le mail de relance${selected.size > 0 ? ` (${selected.size})` : ""}`}
                    </button>
                </>
            )}
        </motion.div>
    );
}
